import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getCompanyOnboarding, saveCompanyOnboardingGoal, type CompanyOnboardingGoal, type CompanyOnboardingProgress } from "./api";
import { useAuth } from "./auth";
import { TargetIcon } from "./Icons";

const goalOptions:Array<{value:CompanyOnboardingGoal;label:string}>=[
  {value:"find_work",label:"Find work for my vehicles"},
  {value:"share_loads",label:"Share loads with trusted carriers"},
  {value:"manage_subcontractors",label:"Manage my subcontractors"},
  {value:"full_operation",label:"Manage my fleet, loads and subcontractors"}
];

type Step={id:string;label:string;to:string;complete:boolean};
type Section={title:string;steps:Step[]};
type Star={id:string;label:string;to:string;earned:boolean};

function model(progress:CompanyOnboardingProgress):{sections:Section[];stars:Star[]} {
  const f=progress.facts;
  if(progress.goal==="find_work")return {
    sections:[
      {title:"Foundation",steps:[
        {id:"profile",label:"Complete company and operational profile",to:"/company/settings",complete:f.profileComplete},
        {id:"vehicle",label:"Add a vehicle with its class, body and capabilities",to:"/vehicles",complete:f.vehicleAdded},
        {id:"group",label:"Join a relevant private network",to:"/groups",complete:f.groupConnected}
      ]},
      {title:"First real use",steps:[
        {id:"browse",label:"Review loads matched to your vehicles",to:"/loads",complete:f.bidPlaced},
        {id:"bid",label:"Place your first bid or counter-offer",to:"/loads",complete:f.bidPlaced},
        {id:"notifications",label:"Configure work and message notifications",to:"/dashboard",complete:f.notificationConfigured}
      ]}
    ],
    stars:[
      {id:"insurance",label:"Add insurance and evidence",to:"/vehicles",earned:f.insuranceAdded},
      {id:"compliance",label:"Add driver or compliance details where applicable",to:"/company/users",earned:f.driverComplianceAdded},
      {id:"completion",label:"Complete an assigned load, POD and invoice",to:"/assigned-loads",earned:f.jobCompleted&&f.podAdded&&f.invoiceCreated}
    ]
  };
  if(progress.goal==="share_loads")return {
    sections:[
      {title:"Foundation",steps:[
        {id:"profile",label:"Complete company and operational profile",to:"/company/settings",complete:f.profileComplete},
        {id:"group",label:"Join or create a suitable private network",to:"/groups",complete:f.groupConnected},
        {id:"address",label:"Add a regular collection or delivery location",to:"/company/settings",complete:f.addressAdded}
      ]},
      {title:"First real use",steps:[
        {id:"post",label:"Post your first structured load",to:"/loads",complete:f.loadPosted},
        {id:"share",label:"Share it with the correct network",to:"/posted-loads",complete:f.loadShared},
        {id:"review",label:"Review a response and assign the work",to:"/posted-loads",complete:f.bidReviewed}
      ]}
    ],
    stars:[
      {id:"addresses",label:"Build an address book with repeat sites",to:"/company/settings",earned:f.addressAdded},
      {id:"team",label:"Invite a teammate or trusted carrier",to:"/company/users",earned:f.teamMemberInvited},
      {id:"completion",label:"Complete a load through POD and invoice",to:"/posted-loads",earned:f.jobCompleted&&f.podAdded&&f.invoiceCreated}
    ]
  };
  if(progress.goal==="manage_subcontractors")return {
    sections:[
      {title:"Foundation",steps:[
        {id:"profile",label:"Complete company and operational profile",to:"/company/settings",complete:f.profileComplete},
        {id:"network",label:"Create a managed subcontractor network",to:"/groups",complete:f.managedGroupCreated},
        {id:"configure",label:"Add network branding, description, rules and requirements",to:"/groups",complete:f.managedGroupConfigured}
      ]},
      {title:"First real use",steps:[
        {id:"invite",label:"Invite your first subcontractor company",to:"/groups",complete:f.subcontractorInvited},
        {id:"offer",label:"Send your first fixed-price offer",to:"/loads",complete:f.fixedOfferPosted},
        {id:"response",label:"Review an acceptance, decline or counter-offer",to:"/posted-loads",complete:f.offerResponseReceived}
      ]}
    ],
    stars:[
      {id:"addresses",label:"Add repeat clients or locations",to:"/company/settings",earned:f.addressAdded},
      {id:"five",label:"Reach five approved subcontractors",to:"/groups",earned:f.fiveSubcontractors},
      {id:"completion",label:"Complete an offer through POD and reporting",to:"/reports",earned:f.jobCompleted&&f.podAdded}
    ]
  };
  return {
    sections:[
      {title:"Foundation",steps:[
        {id:"profile",label:"Complete company and operational profile",to:"/company/settings",complete:f.profileComplete},
        {id:"vehicle",label:"Add your first vehicle and relevant driver",to:"/vehicles",complete:f.vehicleAdded&&f.driverAdded},
        {id:"group",label:"Join or create a private network",to:"/groups",complete:f.groupConnected},
        {id:"managed",label:"Create a managed subcontractor network",to:"/groups",complete:f.managedGroupCreated}
      ]},
      {title:"First real use",steps:[
        {id:"work",label:"Post or respond to your first load",to:"/loads",complete:f.loadPosted||f.bidPlaced},
        {id:"assign",label:"Assign work to your fleet or a subcontractor",to:"/assignments",complete:f.workAssigned},
        {id:"deliver",label:"Track your first job through delivery and POD",to:"/assignments",complete:f.jobCompleted&&f.podAdded}
      ]}
    ],
    stars:[
      {id:"compliance",label:"Add insurance and driver compliance records",to:"/vehicles",earned:f.insuranceAdded&&f.driverComplianceAdded},
      {id:"team",label:"Add repeat locations and another team member",to:"/company/users",earned:f.addressAdded&&f.teamMemberInvited},
      {id:"invoice",label:"Complete your first invoice and operational report",to:"/reports",earned:f.invoiceCreated}
    ]
  };
}

export function CompanyGoalCentre(){
  const auth=useAuth(),company=auth.session?.companies[0];
  const visible=Boolean(company?.roles.some(role=>role==="owner"||role==="manager"));
  const [progress,setProgress]=useState<CompanyOnboardingProgress|null>(null);
  const [open,setOpen]=useState(false),[editing,setEditing]=useState(false),[selection,setSelection]=useState<CompanyOnboardingGoal>("find_work");
  const [saving,setSaving]=useState(false),[error,setError]=useState("");
  useEffect(()=>{if(!visible||!company)return;const refresh=()=>void getCompanyOnboarding(company.companyId).then(value=>{setProgress(value);if(value.goal)setSelection(value.goal);}).catch(()=>setProgress(null));refresh();window.addEventListener("loadsub:onboarding-updated",refresh);return()=>window.removeEventListener("loadsub:onboarding-updated",refresh);},[company?.companyId,visible]);
  const selected=useMemo(()=>progress?.goal?model(progress):null,[progress]);
  const core=selected?.sections.flatMap(section=>section.steps)??[];
  const completed=core.filter(step=>step.complete).length;
  const percentage=core.length?Math.round(completed/core.length*100):0;
  if(!visible||!company||!progress)return null;
  async function save(){
    setSaving(true);setError("");
    try{const value=await saveCompanyOnboardingGoal(company!.companyId,selection);setProgress(value);setEditing(false);}
    catch{setError("Your goal could not be saved. No company data was changed.");}
    finally{setSaving(false);}
  }
  return <div className="company-goal-centre">
    <button className="company-goal-button" type="button" aria-label="Company goal and progress" aria-expanded={open} onClick={()=>setOpen(value=>!value)}>
      <span className="company-goal-mini-progress" aria-hidden="true"><i style={{height:`${percentage}%`}}/></span><TargetIcon/>
      {!progress.goal&&<span className="company-goal-dot" aria-hidden="true"/>}
    </button>
    {open&&<><button className="company-goal-backdrop" type="button" aria-label="Close company goals" onClick={()=>setOpen(false)}/>
      <aside className="company-goal-pane" aria-label="Company goal and progress">
        <header><div><strong>Company goal</strong><small>{progress.goal?`${completed} of ${core.length} core steps complete`:"Choose what you want LoadSub to help you achieve"}</small></div><button type="button" aria-label="Close company goals" onClick={()=>setOpen(false)}>×</button></header>
        {progress.goal&&!editing?<div className="company-goal-selection"><div><small>Current goal</small><strong>{goalOptions.find(option=>option.value===progress.goal)?.label}</strong></div><button type="button" onClick={()=>{setSelection(progress.goal!);setEditing(true)}}>Change goal</button></div>:
          <div className="company-goal-editor"><label htmlFor="company-goal-select">Company goal</label><select id="company-goal-select" value={selection} onChange={event=>setSelection(event.target.value as CompanyOnboardingGoal)}>{goalOptions.map(option=><option key={option.value} value={option.value}>{option.label}</option>)}</select><div><button type="button" disabled={saving} onClick={()=>void save()}>{saving?"Saving…":"Save goal"}</button>{progress.goal&&<button type="button" disabled={saving} onClick={()=>setEditing(false)}>Cancel</button>}</div></div>}
        {progress.goal&&selected&&<><div className="company-goal-progress" role="progressbar" aria-label="Core goal progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percentage}><i style={{width:`${percentage}%`}}/></div>
          <div className="company-goal-content">{selected.sections.map(section=><section key={section.title}><h2>{section.title}</h2><ol>{section.steps.map(step=><li className={step.complete?"is-complete":""} key={step.id}><span aria-hidden="true">{step.complete?"✓":""}</span><strong>{step.label}</strong>{!step.complete&&<Link to={step.to} onClick={()=>{setOpen(false);if(step.id==="notifications")window.setTimeout(()=>window.dispatchEvent(new Event("loadsub:open-notifications")),0)}}>Do this</Link>}</li>)}</ol></section>)}
          <section className="company-goal-stars"><h2>Gold stars</h2><ul>{selected.stars.map(star=><li className={star.earned?"is-earned":""} key={star.id}><span aria-hidden="true">★</span><strong>{star.label}</strong>{!star.earned&&<Link to={star.to} onClick={()=>setOpen(false)}>Optional</Link>}</li>)}</ul></section></div></>}
        {error&&<p className="company-goal-error" role="alert">{error}</p>}
      </aside></>}
  </div>;
}
